import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AlertTriangle, BadgeCheck, Minus, Plus, Star, Leaf, Repeat } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, fileUrl } from "@/lib/api";
import { useStore, needOf } from "@/context/StoreContext";
import { brl, dOnly, digits, maskCep, track, useSeo, SUPP_PHRASES, MED_NOTICE } from "@/lib/fmt";
import BundleOffer from "@/components/store/BundleOffer";
import ProductCard from "@/components/store/ProductCard";
import { BottleStack } from "@/components/store/Bits";

function Gallery({ p, bg }) {
  const slides = [
    ...p.imagens.map((src) => ({ type: "img", src })),
    { type: "card", title: "Benefícios", body: p.alegacoes },
    ...(p.ingredientes || []).map((i) => ({ type: "card", title: i.nome, body: [i.texto] })),
    ...(p.modo_uso ? [{ type: "card", title: "Como usar", body: [p.modo_uso] }] : []),
  ];
  const [i, setI] = useState(0);
  const [zoom, setZoom] = useState(null);
  const s = slides[i];
  return (
    <div data-testid="product-gallery">
      <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-[32px]" style={{ background: bg }}
        onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setZoom(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`); }} onMouseLeave={() => setZoom(null)}>
        {p.tamanho && <span className="absolute left-5 top-5 z-10 rounded-full bg-white px-3 py-1 text-xs font-semibold" data-testid="size-badge">{p.tamanho}</span>}
        {p.off > 0 && <span className="badge-off absolute right-5 top-5 z-10">-{p.off}%</span>}
        {s.type === "img" ? (
          <img src={fileUrl(s.src)} alt={p.nome} className="prod-img h-[85%] object-contain transition-transform duration-200" style={zoom ? { transform: "scale(1.8)", transformOrigin: zoom } : {}} />
        ) : (
          <div className="p-10 text-center sm:p-16"><Leaf className="mx-auto h-8 w-8 text-na-gold" /><h3 className="mt-4 font-serif text-2xl sm:text-3xl">{s.title}</h3>{s.body.map((b) => <p key={b} className="mt-3 text-na-ink2">{b}</p>)}</div>
        )}
      </div>
      <div className="no-scrollbar mt-3 flex gap-2 overflow-x-auto">
        {slides.map((sl, k) => (
          <button key={k} onClick={() => setI(k)} aria-label={`Imagem ${k + 1}`} className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border-2 ${k === i ? "border-na-forest" : "border-transparent"}`} style={{ background: bg }} data-testid={`gallery-thumb-${k}`}>
            {sl.type === "img" ? <img src={fileUrl(sl.src)} alt="" className="prod-img h-12 object-contain" /> : <span className="px-1 text-[9px] font-semibold leading-tight">{sl.title}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function ShippingCalc({ value }) {
  const [cep, setCep] = useState("");
  const [opts, setOpts] = useState(null);
  const calc = async () => {
    if (digits(cep).length !== 8) return;
    const r = await api.get("/shipping/quote", { params: { cep: digits(cep), valor: value } }).catch(() => ({ data: [] }));
    setOpts(r.data);
  };
  return (
    <div className="mt-6 rounded-[22px] border border-na-line bg-white p-4" data-testid="shipping-calculator">
      <label htmlFor="cep-calc" className="label-na">Calcular frete</label>
      <div className="flex gap-2">
        <input id="cep-calc" value={cep} onChange={(e) => setCep(maskCep(e.target.value))} placeholder="00000-000" className="input-na" data-testid="shipping-cep-input" />
        <button onClick={calc} className="btn-line shrink-0 px-5" data-testid="shipping-calc-button">Calcular</button>
      </div>
      {opts && <ul className="mt-3 space-y-1 text-sm" data-testid="shipping-options">{opts.map((o) => <li key={o.servico} className="flex justify-between"><span>{o.nome} · até {o.prazo_dias} dias úteis</span><b>{o.valor ? brl(o.valor) : "Grátis"}</b></li>)}</ul>}
    </div>
  );
}

const Reviews = ({ p }) => (
  <section className="mt-16" data-testid="reviews-section">
    <h2 className="font-serif text-3xl">Avaliações</h2>
    {!p.avaliacoes.length ? <p className="mt-3 text-na-ink2">Ainda não há avaliações. Apenas clientes que compraram podem avaliar.</p> : (
      <>
        <p className="mt-2 flex items-center gap-1 text-sm"><Star className="h-4 w-4 fill-na-gold text-na-gold" /> {p.nota_media} de 5 · {p.avaliacoes.length} avaliações de compradores</p>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {p.avaliacoes.map((r) => (
            <div key={r.id} className="card-na p-6">
              <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= r.nota ? "fill-na-gold text-na-gold" : "text-na-line"}`} />)}</div>
              <p className="mt-3 text-sm">{r.texto}</p>
              <div className="mt-3 flex gap-2">{r.fotos?.map((f) => <img key={f} src={fileUrl(f)} alt="Foto da avaliação" className="h-16 w-16 rounded-xl object-cover" />)}</div>
              <p className="mt-3 text-xs text-na-ink2">{r.cliente_nome} · Compra verificada · {dOnly(r.criado_em)}</p>
              {r.resposta && <p className="mt-3 rounded-2xl bg-na-sage2 p-3 text-xs"><b>Nativa Amazon:</b> {r.resposta}</p>}
            </div>
          ))}
        </div>
      </>
    )}
  </section>
);

export default function Product() {
  const { slug } = useParams();
  const { add, meta, settings } = useStore();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [sub, setSub] = useState(false);
  useSeo(p?.seo?.titulo?.replace(" | Nativa Amazon", "") || p?.nome, p?.seo?.descricao || p?.descricao_curta);
  useEffect(() => {
    setP(null);
    api.get(`/products/${slug}`).then((r) => { setP(r.data); track("view_item", { item_id: slug, value: r.data.preco }); });
  }, [slug]);
  if (!p) return <div className="container-na py-20 text-na-ink2">Carregando...</div>;
  const need = needOf(meta, p);
  const subPct = settings?.assinatura_desconto || 10;
  const unitPrice = sub ? p.preco * (1 - subPct / 100) : p.preco;
  const supp = p.tipo_regulatorio === "Suplemento alimentar";
  const schema = { "@context": "https://schema.org", "@type": "Product", name: p.nome, image: p.imagens.map(fileUrl), description: p.descricao_curta, sku: p.sku, brand: { "@type": "Brand", name: "Nativa Amazon" },
    offers: { "@type": "Offer", priceCurrency: "BRL", price: p.preco, availability: p.estoque > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
    ...(p.nota_media ? { aggregateRating: { "@type": "AggregateRating", ratingValue: p.nota_media, reviewCount: p.avaliacoes.length } } : {}) };
  return (
    <div className="container-na pb-28 pt-8 lg:pb-10">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <nav className="mb-6 text-xs text-na-ink2"><Link to="/">Início</Link> / <Link to={`/categoria/${p.categoria}`}>{p.categoria}</Link> / {p.nome}</nav>
      <div className="grid gap-10 lg:grid-cols-2 lg:gap-16 [&>*]:min-w-0">
        {p.forma === "kit" ? <div className="flex aspect-square items-center justify-center rounded-[32px]" style={{ background: need.cor_fundo }}><BottleStack images={p.imagens} count={p.imagens.length} size="h-56 sm:h-72" /></div> : <Gallery p={p} bg={need.cor_fundo} />}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: need.cor_texto }}>{p.tipo_regulatorio} · {need.nome}</p>
          <h1 className="mt-2 text-4xl sm:text-5xl" data-testid="product-name">{p.nome}</h1>
          {p.nome_cientifico && <p className="mt-2 italic text-na-ink2" data-testid="scientific-name">{p.nome_cientifico}</p>}
          <p className="mt-4 text-na-ink2">{p.descricao_curta}</p>
          <ul className="mt-5 space-y-2">{p.alegacoes.map((a) => <li key={a} className="flex gap-2 text-sm"><Leaf className="mt-0.5 h-4 w-4 shrink-0 text-na-gold" />{a}</li>)}</ul>
          {p.restrito && (
            <div className="mt-5 flex gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" role="alert" data-testid="restriction-alert">
              <AlertTriangle className="h-5 w-5 shrink-0" /><div><b>Advertências:</b> {p.advertencias}</div>
            </div>
          )}
          <div className="mt-6" data-testid="product-price">
            {p.preco_de > p.preco && <span className="mr-2 text-na-ink2 line-through">{brl(p.preco_de)}</span>}
            <span className="text-3xl font-bold">{brl(unitPrice)}</span>
            <p className="text-sm font-semibold text-na-forest">{brl(unitPrice * (1 - (settings?.desconto_pix || 5) / 100))} no Pix</p>
            <p className="text-xs text-na-ink2">ou {settings?.parcelas_max || 3}x de {brl(unitPrice / (settings?.parcelas_max || 3))} sem juros</p>
          </div>
          {p.forma !== "kit" && (
            <div className="mt-5 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Tipo de compra">
              <button onClick={() => setSub(false)} className={`rounded-2xl border p-3 text-left text-sm ${!sub ? "border-na-forest bg-na-sage2" : "border-na-line bg-white"}`} data-testid="purchase-once">Compra única</button>
              <button onClick={() => setSub(true)} className={`rounded-2xl border p-3 text-left text-sm ${sub ? "border-na-forest bg-na-sage2" : "border-na-line bg-white"}`} data-testid="purchase-subscription"><Repeat className="mb-1 h-4 w-4" />Assinar a cada {p.dias_uso} dias · {subPct}% off</button>
            </div>
          )}
          <div className="mt-5 flex gap-3">
            <div className="flex items-center rounded-full border border-na-line bg-white">
              <button className="p-3" onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Diminuir" data-testid="product-qty-minus"><Minus className="h-4 w-4" /></button>
              <span className="w-8 text-center" data-testid="product-qty">{qty}</span>
              <button className="p-3" onClick={() => setQty(qty + 1)} aria-label="Aumentar" data-testid="product-qty-plus"><Plus className="h-4 w-4" /></button>
            </div>
            <button className="btn-na flex-1" disabled={p.estoque <= 0} onClick={() => add(p.slug, 1, qty, sub, "drawer", unitPrice * qty)} data-testid="product-add-button">{p.estoque <= 0 ? "Esgotado" : "Adicionar à sacola"}</button>
          </div>
          <BundleOffer p={p} />
          <ShippingCalc value={p.preco} />
          {p.registro && <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-na-sage2 px-4 py-2 text-xs font-semibold text-na-forest" data-testid="registration-badge"><BadgeCheck className="h-4 w-4" /> {p.registro}</p>}
          <p className="mt-4 text-xs text-na-ink2" data-testid="medical-notice">{MED_NOTICE}</p>
          <Accordion type="multiple" className="mt-6" data-testid="product-accordions">
            <AccordionItem value="uso"><AccordionTrigger>Como usar</AccordionTrigger><AccordionContent>{p.modo_uso || "Veja a embalagem."}</AccordionContent></AccordionItem>
            <AccordionItem value="comp"><AccordionTrigger>Composição e tabela nutricional</AccordionTrigger><AccordionContent>
              <p>{p.composicao}</p>
              {p.kit_itens?.length > 0 && <ul className="mt-2 list-disc pl-5">{p.kit_itens.map((k) => <li key={k.slug}>{k.quantidade}x {k.nome}</li>)}</ul>}
              {p.tabela_nutricional && (
                <table className="mt-3 w-full text-left text-sm" data-testid="nutrition-table">
                  <caption className="mb-2 text-left text-xs">Porção: {p.tabela_nutricional.porcao}</caption>
                  <thead><tr className="border-b"><th className="py-1">Nutriente</th><th>Quantidade</th><th>%VD*</th></tr></thead>
                  <tbody>{p.tabela_nutricional.itens.map((r) => <tr key={r[0]} className="border-b"><td className="py-1">{r[0]}</td><td>{r[1]}</td><td>{r[2]}</td></tr>)}</tbody>
                </table>
              )}
              {p.tabela_nutricional && <p className="mt-1 text-xs">*% Valores Diários de referência com base em uma dieta de 2.000 kcal.</p>}
            </AccordionContent></AccordionItem>
            <AccordionItem value="adv"><AccordionTrigger>Contraindicações e advertências</AccordionTrigger><AccordionContent>
              <p>{p.advertencias || "Leia a embalagem antes de usar."}</p>
              {supp && <ul className="mt-2 space-y-1 font-medium" data-testid="supplement-phrases">{SUPP_PHRASES.map((s) => <li key={s}>{s}</li>)}</ul>}
            </AccordionContent></AccordionItem>
            <AccordionItem value="lote"><AccordionTrigger>Lote, validade e registro</AccordionTrigger><AccordionContent>
              {p.lote_atual ? <p>Lote em expedição: {p.lote_atual.numero} · validade {dOnly(p.lote_atual.validade)}</p> : <p>Kit composto por itens com lotes próprios.</p>}<p>Registro: {p.registro}</p>
            </AccordionContent></AccordionItem>
            <AccordionItem value="entrega"><AccordionTrigger>Entrega e trocas</AccordionTrigger><AccordionContent>Postamos em até 1 dia útil após o pagamento. Você tem 7 dias após o recebimento para desistir (art. 49 do CDC). <Link className="underline" to="/paginas/trocas-e-devolucoes">Saiba mais</Link>.</AccordionContent></AccordionItem>
          </Accordion>
        </div>
      </div>
      {p.relacionados_produtos?.length > 0 && (
        <section className="mt-16"><h2 className="font-serif text-3xl">Combina com</h2><div className="mt-6 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">{p.relacionados_produtos.map((r) => <ProductCard key={r.slug} p={r} />)}</div></section>
      )}
      <Reviews p={p} />
      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-na-line bg-white/95 px-4 py-3 backdrop-blur-xl lg:hidden" data-testid="mobile-buy-bar">
        <div className="flex-1 leading-tight">
          {p.preco_de > p.preco && <span className="text-xs text-na-ink2 line-through">{brl(p.preco_de)}</span>}
          <p className="text-lg font-bold">{brl(unitPrice)}</p>
          <p className="text-[11px] text-na-ink2">{settings?.parcelas_max || 3}x de {brl(unitPrice / (settings?.parcelas_max || 3))}</p>
        </div>
        <button className="btn-na" disabled={p.estoque <= 0} onClick={() => add(p.slug, 1, qty, sub, "drawer", unitPrice)} data-testid="mobile-buy-button">Comprar</button>
      </div>
    </div>
  );
}
